import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { ClipboardCheck, CheckCircle2, AlertTriangle, History, UserCheck, Star } from "lucide-react";
import { LeadAttendanceHistory } from "@/components/LeadAttendanceHistory";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, EmptyState } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const STATUS = [
  ["present", "Present", "bg-teal text-white"],
  ["late", "Late", "bg-yellow-400 text-yellow-900"],
  ["absent", "Absent", "bg-red-500 text-white"],
  ["excused", "Excused", "bg-blue-400 text-white"],
  ["observing", "Observing", "bg-gray-300 text-gray-700"],
];
const iso = (d) => d.toISOString().slice(0, 10);

export default function Attendance() {
  const { user } = useAuth();
  const [classes, setClasses] = useState([]);
  const [classId, setClassId] = useState("");
  const [date, setDate] = useState(iso(new Date()));
  const [roster, setRoster] = useState([]);
  const [classNote, setClassNote] = useState("");
  const [complete, setComplete] = useState(false);
  const [absentees, setAbsentees] = useState([]);
  const [tab, setTab] = useState(() => (new URLSearchParams(window.location.search).get("tab") === "lead" ? "lead" : "take"));
  const [hist, setHist] = useState([]);
  const [hClass, setHClass] = useState("");
  const [hDancer, setHDancer] = useState("");
  const [hFrom, setHFrom] = useState("");
  const [hTo, setHTo] = useState("");
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    api.get("/classes").then((r) => { setClasses(r.data); if (r.data[0]) setClassId(r.data[0].id); }).catch(() => {});
    api.get("/attendance/report/absences").then((r) => setAbsentees(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (tab !== "history") return;
    const params = {};
    if (hClass) params.class_id = hClass;
    if (hDancer) params.dancer_id = hDancer;
    if (hFrom) params.date_from = hFrom;
    if (hTo) params.date_to = hTo;
    api.get("/attendance/history", { params }).then((r) => setHist(r.data)).catch(() => setHist([]));
  }, [tab, hClass, hDancer, hFrom, hTo]);

  const dancerOptions = React.useMemo(() => {
    const m = new Map();
    hist.forEach((h) => (h.records || []).forEach((r) => { if (r.dancer_name) m.set(r.dancer_id, r.dancer_name); }));
    return [...m.entries()].sort((a, b) => a[1].localeCompare(b[1]));
  }, [hist]);

  const loadRoster = () => {
    if (!classId || !date) return;
    api.get("/attendance/roster", { params: { class_id: classId, date } })
      .then((r) => { setRoster(r.data.roster); setClassNote(r.data.class_note || ""); setComplete(r.data.complete); })
      .catch((e) => { setRoster([]); toast.error(e.response?.data?.detail || "Can't load roster"); });
  };
  useEffect(() => { loadRoster(); }, [classId, date]);

  const setStatus = (id, status) => setRoster((r) => r.map((x) => x.dancer_id === id ? { ...x, status } : x));
  const setArrival = (id, t) => setRoster((r) => r.map((x) => x.dancer_id === id ? { ...x, arrival_time: t } : x));
  const markAll = () => setRoster((r) => r.map((x) => ({ ...x, status: "present" })));
  const save = async (markComplete) => {
    try {
      await api.post("/attendance", { class_id: classId, date, class_note: classNote, complete: markComplete,
        records: roster.filter((x) => x.status).map((x) => ({ dancer_id: x.dancer_id, status: x.status, note: x.note || "", arrival_time: x.arrival_time || "" })) });
      toast.success(markComplete ? "Attendance complete" : "Saved"); setComplete(markComplete);
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  return (
    <div className="stagger-in">
      <PageHeader title="Attendance" subtitle="Mark who's in class today." testid="attendance-page" />
      <div className="flex flex-wrap gap-2 mb-5">
        <Button variant={tab === "take" ? "default" : "outline"} className={`rounded-full ${tab === "take" ? "bg-teal hover:bg-teal-dark" : ""}`} data-testid="attendance-tab-take" onClick={() => setTab("take")}><ClipboardCheck className="w-4 h-4 mr-2" />Take attendance</Button>
        <Button variant={tab === "history" ? "default" : "outline"} className={`rounded-full ${tab === "history" ? "bg-teal hover:bg-teal-dark" : ""}`} data-testid="attendance-tab-history" onClick={() => setTab("history")}><History className="w-4 h-4 mr-2" />History</Button>
        <Button variant={tab === "lead" ? "default" : "outline"} className={`rounded-full ${tab === "lead" ? "bg-teal hover:bg-teal-dark" : ""}`} data-testid="attendance-tab-lead" onClick={() => setTab("lead")}><Star className="w-4 h-4 mr-2" />Lead practices</Button>
      </div>
      {tab === "lead" && <LeadAttendanceHistory />}

      {tab === "take" && <>
      <div className="flex flex-wrap items-end gap-3 mb-5">
        <div className="space-y-1.5"><Label>Class</Label>
          <select data-testid="attendance-class" className="border rounded-md h-10 px-3 bg-white min-w-[200px]" value={classId} onChange={(e) => setClassId(e.target.value)}>
            {classes.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select></div>
        <div className="space-y-1.5"><Label>Date</Label><Input data-testid="attendance-date" type="date" className="w-44" value={date} onChange={(e) => setDate(e.target.value)} /></div>
        <Button variant="outline" className="rounded-full" data-testid="mark-all-present" onClick={markAll}>Mark all present</Button>
        {complete && <span className="text-sm text-teal-dark flex items-center gap-1"><CheckCircle2 className="w-4 h-4" />Complete</span>}
      </div>

      {roster.length === 0 ? <EmptyState icon={ClipboardCheck} title="No dancers on this roster" hint="Pick a class you teach or are covering." testid="attendance-empty" /> :
        <div className="bg-white rounded-2xl border border-gray-100 divide-y">
          {roster.map((s) => (
            <div key={s.dancer_id} data-testid={`att-row-${s.dancer_id}`} className="flex items-center justify-between gap-3 p-3 flex-wrap">
              <p className="font-semibold">{s.name}</p>
              <div className="flex gap-1.5 flex-wrap items-center">
                {STATUS.map(([k, label, c]) => (
                  <button key={k} data-testid={`att-${s.dancer_id}-${k}`} onClick={() => setStatus(s.dancer_id, k)}
                    className={`text-xs rounded-full px-3 py-1 font-semibold transition-colors ${s.status === k ? c : "bg-background text-muted-foreground hover:bg-gray-100"}`}>{label}</button>
                ))}
                {s.status === "late" && <Input type="time" data-testid={`att-arrival-${s.dancer_id}`} value={s.arrival_time || ""} onChange={(e) => setArrival(s.dancer_id, e.target.value)} className="h-8 w-28 text-xs" title="Arrival time" />}
              </div>
            </div>
          ))}
        </div>}

      {roster.length > 0 && <div className="mt-4 space-y-3">
        <div className="space-y-1.5"><Label>Class note (optional)</Label><Input data-testid="attendance-note" value={classNote} onChange={(e) => setClassNote(e.target.value)} /></div>
        <div className="flex gap-2">
          <Button variant="outline" className="rounded-full" data-testid="attendance-save" onClick={() => save(false)}>Save draft</Button>
          <Button className="rounded-full bg-teal hover:bg-teal-dark" data-testid="attendance-complete" onClick={() => save(true)}>Attendance complete</Button>
        </div>
      </div>}

      {absentees.length > 0 && <div className="mt-8">
        <h3 className="font-head font-bold flex items-center gap-2 mb-3"><AlertTriangle className="w-4 h-4 text-yellow-600" />Repeat absences</h3>
        <div className="bg-white rounded-2xl border border-gray-100 divide-y" data-testid="absence-report">
          {absentees.map((a) => <div key={a.dancer_id} className="flex justify-between p-3"><span className="font-semibold">{a.name}</span><span className="text-red-600 font-bold">{a.absences} absences</span></div>)}
        </div>
      </div>}
      </>}

      {tab === "history" && <>
        <div className="flex flex-wrap items-end gap-3 mb-5">
          <div className="space-y-1.5"><Label>Class</Label>
            <select data-testid="history-class" className="border rounded-md h-10 px-3 bg-white min-w-[180px]" value={hClass} onChange={(e) => setHClass(e.target.value)}>
              <option value="">All classes</option>
              {classes.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select></div>
          <div className="space-y-1.5"><Label>Dancer</Label>
            <select data-testid="history-dancer" className="border rounded-md h-10 px-3 bg-white min-w-[160px]" value={hDancer} onChange={(e) => setHDancer(e.target.value)}>
              <option value="">All dancers</option>
              {dancerOptions.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
            </select></div>
          <div className="space-y-1.5"><Label>From</Label><Input data-testid="history-from" type="date" className="w-40" value={hFrom} onChange={(e) => setHFrom(e.target.value)} /></div>
          <div className="space-y-1.5"><Label>To</Label><Input data-testid="history-to" type="date" className="w-40" value={hTo} onChange={(e) => setHTo(e.target.value)} /></div>
          {(hClass || hDancer || hFrom || hTo) && <Button variant="outline" className="rounded-full" data-testid="history-clear" onClick={() => { setHClass(""); setHDancer(""); setHFrom(""); setHTo(""); }}>Clear</Button>}
        </div>
        {hist.length === 0 ? <EmptyState icon={History} title="No attendance recorded yet" hint="Saved attendance sessions will appear here." testid="history-empty" /> :
          <div className="space-y-2" data-testid="attendance-history">
            {hist.map((h) => {
              const open = expanded === h.id;
              return (
              <div key={h.id} data-testid={`history-row-${h.id}`} className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
                <button className="w-full text-left flex items-center justify-between gap-3 p-4 flex-wrap hover:bg-gray-50" data-testid={`history-toggle-${h.id}`} onClick={() => setExpanded(open ? null : h.id)}>
                  <div>
                    <p className="font-semibold">{h.class_name || "Class"} · {h.date}</p>
                    <p className="text-sm text-muted-foreground flex items-center gap-1 mt-0.5">
                      <UserCheck className="w-3.5 h-3.5 text-teal" />Recorded by <b data-testid={`history-takenby-${h.id}`}>{h.taken_by_name || "Unknown"}</b>
                      {h.complete && <span className="ml-2 inline-flex items-center gap-1 text-teal-dark"><CheckCircle2 className="w-3.5 h-3.5" />Complete</span>}
                      {(h.edits || []).length > 0 && <span className="ml-2 text-amber-700">· edited {h.edits.length}×</span>}
                    </p>
                  </div>
                  <div className="flex gap-2 text-sm">
                    <span className="rounded-full bg-teal/10 text-teal-dark px-3 py-1 font-semibold">{h.present} present</span>
                    <span className="rounded-full bg-yellow-50 text-yellow-700 px-3 py-1 font-semibold">{h.late} late</span>
                    <span className="rounded-full bg-red-50 text-red-600 px-3 py-1 font-semibold">{h.absent} absent</span>
                  </div>
                </button>
                {open && <div className="border-t divide-y" data-testid={`history-detail-${h.id}`}>
                  {(h.records || []).length === 0 ? <p className="p-3 text-sm text-muted-foreground">No individual records saved for this session.</p> :
                    h.records.map((r) => { const st = STATUS.find((s) => s[0] === r.status); return (
                      <div key={r.dancer_id} data-testid={`history-rec-${h.id}-${r.dancer_id}`} className="flex items-center justify-between gap-3 p-3">
                        <span className="font-medium">{r.dancer_name || "Dancer"}</span>
                        <span className="flex items-center gap-2">
                          {r.status === "late" && r.arrival_time && <span className="text-xs text-muted-foreground">arrived {r.arrival_time}</span>}
                          {r.note && <span className="text-xs text-muted-foreground italic">{r.note}</span>}
                          <span className={`text-xs rounded-full px-3 py-1 font-semibold ${st ? st[2] : "bg-gray-200 text-gray-600"}`}>{st ? st[1] : r.status}</span>
                        </span>
                      </div>
                    ); })}
                </div>}
              </div>
            ); })}
          </div>}
      </>}
    </div>
  );
}
